"use client"

import * as React from "react"
import ReactMarkdown from 'react-markdown'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Bot } from "lucide-react"
import { getParentLogoUrl } from '../../services/logo.service'
import { useRTL } from '../../contexts/RTLContext'
import { getMessageAlignment, getTextAlignment } from '../../utils/rtl'
import { trackLinkClicked } from '../../services/analytics.service'

// Utility function for className merging
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

function MessageLoading() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className="text-foreground"
    >
      <circle cx="4" cy="12" r="2" fill="currentColor">
        <animate
          id="spinner_qFRN"
          begin="0;spinner_OcgL.end+0.25s"
          attributeName="cy"
          calcMode="spline"
          dur="0.6s"
          values="12;6;12"
          keySplines=".33,.66,.66,1;.33,0,.66,.33"
        />
      </circle>
      <circle cx="12" cy="12" r="2" fill="currentColor">
        <animate
          begin="spinner_qFRN.begin+0.1s"
          attributeName="cy"
          calcMode="spline"
          dur="0.6s"
          values="12;6;12"
          keySplines=".33,.66,.66,1;.33,0,.66,.33"
        />
      </circle>
      <circle cx="20" cy="12" r="2" fill="currentColor">
        <animate
          id="spinner_OcgL"
          begin="spinner_qFRN.begin+0.2s"
          attributeName="cy"
          calcMode="spline"
          dur="0.6s"
          values="12;6;12"
          keySplines=".33,.66,.66,1;.33,0,.66,.33"
        />
      </circle>
    </svg>
  )
}

interface AIMessageProps {
  content: string
  parts?: any[] // Parts for tool calls and other message components
  timestamp: Date
  isLoading?: boolean
  avatar?: string
  className?: string
  projectIconUrl?: string | null
  projectId: string
  threadId: string
  messages?: any[]
}

export function AIMessage({ 
  content,
  parts = [],
  timestamp,
  isLoading = false,
  avatar,
  className,
  projectIconUrl,
  projectId,
  threadId,
  messages = []
}: AIMessageProps) {
  const { isRTL } = useRTL()
  
  const timeString = timestamp.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit' 
  })

  // Get logo URL with project icon priority, then parent logo, then custom avatar
  const logoUrl = avatar || (projectIconUrl ? projectIconUrl : getParentLogoUrl(32, 32))
  
  // Debug logging
  React.useEffect(() => {
    console.log('AIMessage - projectIconUrl:', projectIconUrl)
    console.log('AIMessage - logoUrl:', logoUrl)
  }, [projectIconUrl, logoUrl])
  
  // Get RTL-aware classes
  const messageAlignment = getMessageAlignment(false, isRTL) // false = AI message
  const textAlignment = getTextAlignment(isRTL)
  
  // Handle link clicks for analytics
  const handleLinkClick = (url: string, text: string) => {
    trackLinkClicked(projectId, threadId, url, text, messages)
  }

  return (
    <div className={cn("flex items-start gap-3 mb-6", messageAlignment, className)}>
      <Avatar className="h-8 w-8 shrink-0">
        {logoUrl && (
          <AvatarImage 
            src={logoUrl} 
            alt="AI Assistant"
            className="object-contain"
            onError={(e) => {
              console.error('AIMessage - Image failed to load:', logoUrl, e)
            }}
            onLoad={() => {
              console.log('AIMessage - Image loaded successfully:', logoUrl)
            }}
          />
        )}
        <AvatarFallback className="bg-muted">
          <Bot className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0 overflow-hidden">
        <div className={cn("prose prose-sm max-w-none text-foreground", textAlignment)}>
          {isLoading ? (
            <div className={cn("flex items-center py-2", isRTL ? 'justify-end' : 'justify-start')}>
              <MessageLoading />
            </div>
          ) : (
            <div className="space-y-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
              {/* Render text parts if they exist, otherwise fall back to content */}
              {parts.some(part => part.type === 'text') ? (
                parts.map((part, index) => {
                  if (part.type === 'text') {
                    return (
                      <div 
                        key={`text-${index}`} 
                        className="leading-relaxed break-words max-w-full overflow-hidden"
                        style={{ 
                          overflowWrap: 'anywhere', 
                          wordBreak: 'break-word',
                          direction: isRTL ? 'rtl' : 'ltr',
                          textAlign: isRTL ? 'right' : 'left'
                        }}
                        dir={isRTL ? 'rtl' : 'ltr'}
                      >
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                            strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                            a: ({ href, children }) => (
                              <a 
                                href={href} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-blue-600 hover:text-blue-800 underline"
                                onClick={() => handleLinkClick(href || '', children?.toString() || '')}
                              >
                                {children}
                              </a>
                            ),
                            ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                            ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                            li: ({ children }) => <li className="">{children}</li>
                          }}
                        >
                          {part.text}
                        </ReactMarkdown>
                      </div>
                    );
                  }
                  return null;
                })
              ) : content ? (
                <div 
                  className="text-sm leading-relaxed break-words max-w-full overflow-hidden"
                  style={{ 
                    overflowWrap: 'anywhere', 
                    wordBreak: 'break-word',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                  dir={isRTL ? 'rtl' : 'ltr'}
                >
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      a: ({ href, children }) => (
                        <a 
                          href={href} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:text-blue-800 underline"
                          onClick={() => handleLinkClick(href || '', children?.toString() || '')}
                        >
                          {children}
                        </a>
                      ),
                      ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="text-sm">{children}</li>
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              ) : null}
            </div>
          )}
        </div>
        {!isLoading && (
          <span className={cn("text-xs text-muted-foreground mt-2 block", textAlignment)}>{timeString}</span>
        )}
      </div>
    </div>
  )
} 